import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Loader2, CheckCircle, XCircle, Eye, EyeOff, Lock, Shield } from 'lucide-react';

const SetupPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { login } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const name = searchParams.get('name');
  const isNewUser = searchParams.get('new') === 'true';

  // Password validation
  const passwordChecks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    passwordsMatch: password === confirmPassword && password.length > 0
  };
  
  const isPasswordStrong = passwordChecks.minLength && 
    passwordChecks.hasUppercase && 
    passwordChecks.hasLowercase && 
    passwordChecks.hasNumber;

  useEffect(() => {
    // Verify token on mount
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-setup-token/${token}`
        );
        
        if (response.ok) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
        }
      } catch (error) {
        console.error('Token verification error:', error);
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordStrong) {
      toast({
        title: 'Weak Password',
        description: 'Please meet all password requirements',
        variant: 'destructive'
      });
      return;
    }
    
    if (!passwordChecks.passwordsMatch) {
      toast({
        title: 'Password Mismatch',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/setup-password?token=${token}&password=${encodeURIComponent(password)}&confirm_password=${encodeURIComponent(confirmPassword)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to setup password');
      }
      
      const data = await response.json();
      
      // Login user
      login(data.user, data.access_token);
      
      toast({
        title: 'Password Set Successfully!',
        description: 'Your account is now ready. Redirecting to dashboard...',
      });
      
      // Redirect based on user role and registration status
      setTimeout(() => {
        if (data.user.role === 'vendor' && data.user.registration_step < 5) {
          navigate('/vendor/tax-registration');
        } else {
          navigate('/dashboard');
        }
      }, 1500);
      
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-600">Verifying your session...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <XCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid or Expired Link</h2>
            <p className="text-gray-600 text-center mb-6">
              This password setup link is invalid or has expired. Please sign in again.
            </p>
            <Button onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {isNewUser ? 'Complete Your Registration' : 'Set Your Password'}
          </CardTitle>
          <CardDescription>
            {isNewUser 
              ? `Welcome ${name || ''}! Create a password to secure your account.`
              : 'Create a secure password for your account'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Email Display */}
          <div className="bg-gray-50 rounded-lg p-3 mb-6">
            <p className="text-sm text-gray-500">Account Email (Username)</p>
            <p className="font-medium text-gray-900">{email}</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Password Requirements */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
              <PasswordCheck met={passwordChecks.minLength} text="At least 8 characters" />
              <PasswordCheck met={passwordChecks.hasUppercase} text="One uppercase letter" />
              <PasswordCheck met={passwordChecks.hasLowercase} text="One lowercase letter" />
              <PasswordCheck met={passwordChecks.hasNumber} text="One number" />
              <PasswordCheck met={passwordChecks.hasSpecial} text="One special character (optional)" optional />
              <div className="pt-2 border-t mt-2">
                <PasswordCheck met={passwordChecks.passwordsMatch} text="Passwords match" />
              </div>
            </div>
            
            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              size="lg"
              disabled={loading || !isPasswordStrong || !passwordChecks.passwordsMatch}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Complete Setup
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Password requirement check component
const PasswordCheck = ({ met, text, optional = false }) => (
  <div className="flex items-center gap-2">
    {met ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <div className={`h-4 w-4 rounded-full border-2 ${optional ? 'border-gray-300' : 'border-gray-300'}`} />
    )}
    <span className={`text-sm ${met ? 'text-green-700' : optional ? 'text-gray-500' : 'text-gray-600'}`}>
      {text}
    </span>
  </div>
);

export default SetupPassword;
